import React, { useState, useEffect } from "react";
import { db, auth, storage } from "../../../backend/firebaseConfig";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import {
  onSnapshot,
  collection,
  getDocs,
  query,
  where,
  getDoc,
  doc,
} from "firebase/firestore";
import { ref, getDownloadURL, listAll } from "firebase/storage";
import ViewInsurance from "../../../components/UI/organisms/insurance/ViewInsurance";
import CustomAlert from "../../../components/UI/molecules/alert/CustomAlert";
import { PLACEHOLDERS } from "../../../utils/placeholders";

import Modal from "react-modal";

// Import Atoms
import ButtonComp from "../../../components/UI/atoms/button/Button";
import SplashScreen from "../../../components/UI/atoms/splash/SplashScreen";

// Imports MUI Components
import {
  Box,
  Button,
  Stack,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  AvatarGroup,
  Avatar,
  Pagination,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  IconButton,
  Slide,
  Fade
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";


// Import Organisms
import Sidebar from "../../../components/UI/organisms/sidebar/Sidebar";
import Header from "../../../components/UI/organisms/header/logged-in/HeaderLoggedIn";

// Import Functions
import { notifyFirstUser } from "../../../components/functions/notifyFirstUser";
import { redirectIfNotLoggedIn } from "../../../components/functions/redirectIfNotLoggedIn";

import "./styles.css";
import Text from "../../../components/UI/atoms/text/Text";
import Icon from "../../../components/UI/atoms/icon/Icon";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

function formatDateMMDDYYYY(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${month}-${day}-${year}`;
}

const PatientRecord = () => {
  // Function to remove parentheses from dentist names
  const formatDentistName = (dentistName) => {
    if (!dentistName) return "N/A";
    // Remove (Owner) and (Associate) from dentist names
    return dentistName.replace(/\s*\(Owner\)|\s*\(Associate\)/g, "");
  };

  const [appointments, setAppointments] = useState([]);
  const [insuranceDetails, setInsuranceDetails] = useState(null);
  const [showInsuranceForm, setShowInsuranceForm] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  // For selected treatment details
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [loading, setLoading] = useState(true);

  // For image viewing
  const [treatmentImages, setTreatmentImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  const [showCustomAlert, setShowCustomAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [alertImage, setAlertImage] = useState("");
  const [alertButton, setAlertButton] = useState("");
  const [alertButtonType, setAlertButtonType] = useState("false");
  const [alertLink, setAlertLink] = useState("");
  const [isScheduleLoading, setIsScheduleLoading] = useState(false);

  const [showTooltip, setShowTooltip] = React.useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

  React.useEffect(() => {
    if (isSmallScreen) {
      setShowTooltip(true);
    } else {
      setShowTooltip(false); 
    }
  }, [isSmallScreen]);

  const handleTooltipClose = () => {
    setShowTooltip(false);
  };

  // Button click handlers

  // For custom alerts
  const [alertConfig, setAlertConfig] = useState({
    isOpen: false,
    message: "",
    type: "info",
    title: null,
  });

  // For dentist profile picture
  const [, setDentistProfilePicture] = useState(null);

  // Show custom alert
  const showAlert = (message, type = "info", title = null) => {
    setAlertConfig({
      isOpen: true,
      message,
      type,
      title,
    });
  };

  // Close custom alert
  const closeAlert = () => {
    setAlertConfig((prev) => ({ ...prev, isOpen: false }));
  };

  // UseEffects
  useEffect(() => {
    // const details = fetchUserDetails(setUserDetails);
    const redirect = redirectIfNotLoggedIn(setShowCustomAlert);
    const notify = notifyFirstUser();
    return () => {
      // details && details();
      redirect && redirect();
      notify && notify();
    };
  }, []);

  // Fetch user and appointments
  useEffect(() => {
    let unsubscribeAppointments = null;

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchUserDetails(setUserDetails, user.uid);
        unsubscribeAppointments = fetchCompletedAppointments(user.uid);
      } else {
        setAppointments([]);
        setUserDetails(null);
        setLoading(true);
        // Unsubscribe from appointments if active
        if (unsubscribeAppointments) {
          unsubscribeAppointments();
          unsubscribeAppointments = null;
        }
      }
    });

    return () => {
      unsubscribe();
      if (unsubscribeAppointments) {
        unsubscribeAppointments();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Load images when selected treatment changes
  useEffect(() => {
    if (selectedTreatment) {
      loadTreatmentImages();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTreatment]);

  // Keep selected treatment in sync with latest appointment data
  useEffect(() => {
    if (!selectedTreatment) return;

    const latest = appointments.find(
      (appt) => appt.id === selectedTreatment.id && appt.date === selectedTreatment.date
    );

    if (latest) {
      setSelectedTreatment((prev) => {
        if (!prev) return latest;
        const imagesChanged =
          JSON.stringify(prev.images || []) !== JSON.stringify(latest.images || []);
        const remarksChanged = prev.dentistRemarks !== latest.dentistRemarks;
        if (imagesChanged || remarksChanged) {
          return latest;
        }
        return prev;
      });
    }
  }, [appointments, selectedTreatment]);

  // Load dentist profile picture when selected treatment changes
  useEffect(() => {
    if (selectedTreatment && selectedTreatment.dentist) {
      loadDentistProfilePicture();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTreatment]);

  // Load dentist profile picture from Firebase
  const loadDentistProfilePicture = async () => {
    if (!selectedTreatment?.dentist) return;

    try {
      // Clean the dentist name by removing extra text
      let cleanDentistName = selectedTreatment.dentist;
      if (cleanDentistName.includes("(")) {
        cleanDentistName = cleanDentistName.split("(")[0].trim();
      }
      if (cleanDentistName.startsWith("Dr. ")) {
        cleanDentistName = cleanDentistName.substring(4).trim();
      }

      // Search for dentist user in users collection
      const usersRef = collection(db, "users");
      const q = query(
        usersRef,
        where("firstName", "==", cleanDentistName.split(" ")[0])
      );
      const querySnapshot = await getDocs(q);

      let dentistData = null;
      querySnapshot.forEach((doc) => {
        const userData = doc.data();

        // Check if this user is a dentist and matches the name
        if (
          userData.userType === "DentistOwner" ||
          userData.userType === "AssociateDentist" ||
          userData.role === "DentistOwner" ||
          userData.role === "AssociateDentist"
        ) {
          const fullName = userData.firstName + " " + userData.lastName;

          if (fullName === cleanDentistName) {
            dentistData = userData;
          }
        }
      });

      if (dentistData && dentistData.profilePictureUrl) {
        setDentistProfilePicture(dentistData.profilePictureUrl);
      } else {
        setDentistProfilePicture(null);
      }

      // If no profile picture found, try a broader search
      if (!dentistData) {
        const allUsersSnapshot = await getDocs(usersRef);
        allUsersSnapshot.forEach((doc) => {
          const userData = doc.data();
          if (
            userData.userType === "DentistOwner" ||
            userData.userType === "AssociateDentist" ||
            userData.role === "DentistOwner" ||
            userData.role === "AssociateDentist"
          ) {

            // Try partial matching with cleaned name
            if (
              cleanDentistName.includes(userData.firstName) ||
              cleanDentistName.includes(userData.lastName)
            ) {
              if (userData.profilePictureUrl) {
                setDentistProfilePicture(userData.profilePictureUrl);
                return;
              }
            }
          }
        });
      }
    } catch (error) {
      console.error("Error loading dentist profile picture:", error);
      setDentistProfilePicture(null);
    }
  };

  // Keyboard navigation for image modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showImageModal || treatmentImages.length <= 1) return;

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          const currentIndex = treatmentImages.findIndex(
            (img) => img.path === selectedImage?.path
          );
          const prevIndex =
            currentIndex === 0 ? treatmentImages.length - 1 : currentIndex - 1;
          setSelectedImage(treatmentImages[prevIndex]);
          break;
        case "ArrowRight":
          e.preventDefault();
          const currentIdx = treatmentImages.findIndex(
            (img) => img.path === selectedImage?.path
          );
          const nextIndex =
            currentIdx === treatmentImages.length - 1 ? 0 : currentIdx + 1;
          setSelectedImage(treatmentImages[nextIndex]);
          break;
        case "Escape":
          setShowImageModal(false);
          break;
        default:
          break;
      }
    };

    if (showImageModal) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showImageModal, treatmentImages, selectedImage]);

  // Load treatment images from Firebase Storage
  const loadTreatmentImages = async () => {
    if (!selectedTreatment) {
      setTreatmentImages([]);
      return;
    }

    setLoadingImages(true);
    setTreatmentImages([]);

    const resolveImageUrls = async (references = []) => {
      const results = await Promise.all(
        references.map(async (refItem) => {
          const storagePath = refItem?.path || refItem?.storagePath;
          if (!storagePath) return null;

          try {
            const url = await getDownloadURL(ref(storage, storagePath));
            return { name: refItem.name || storagePath, url, path: storagePath };
          } catch (error) {
            try {
              const encodedPath = storagePath
                .split("/")
                .map((segment) => encodeURIComponent(segment))
                .join("%2F");
              const bucket = storage.app.options.storageBucket;
              const fallbackUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
              return { name: refItem.name || storagePath, url: fallbackUrl, path: storagePath };
            } catch (fallbackError) {
              return null;
            }
          }
        })
      );
      return results.filter(Boolean);
    };

    try {
      const candidateAppointmentIds = Array.from(
        new Set(
          [
            selectedTreatment.id,
            selectedTreatment.appointmentId,
            selectedTreatment.originalId,
          ].filter(Boolean)
        )
      );

      const patientIdCandidates = Array.from(
        new Set(
          [
            selectedTreatment.patientId,
            selectedTreatment.userId,
            selectedTreatment.uid,
            userDetails?.uid,
          ].filter(Boolean)
        )
      );

      let imageReferences = Array.isArray(selectedTreatment.images)
        ? selectedTreatment.images
        : [];

      if (imageReferences.length === 0 && selectedTreatment.date) {
        const appointmentDocRef = doc(db, "appointments", selectedTreatment.date);
        const appointmentDoc = await getDoc(appointmentDocRef);
        if (appointmentDoc.exists()) {
          const appointmentCollectionData = appointmentDoc.data();

          for (const appointmentId of candidateAppointmentIds) {
            const directMatch = appointmentCollectionData?.[appointmentId];
            if (directMatch?.images && Array.isArray(directMatch.images)) {
              imageReferences = directMatch.images;
              break;
            }

            for (const patientId of patientIdCandidates) {
              const nestedMatch =
                appointmentCollectionData?.[patientId]?.[appointmentId];
              if (nestedMatch?.images && Array.isArray(nestedMatch.images)) {
                imageReferences = nestedMatch.images;
                break;
              }
            }

            if (imageReferences.length > 0) break;
          }

          if (imageReferences.length > 0) {
            setSelectedTreatment((prev) =>
              prev ? { ...prev, images: imageReferences } : prev
            );
          }
        }
      }

      if (imageReferences.length > 0) {
        const resolvedImages = await resolveImageUrls(imageReferences);
        if (resolvedImages.length > 0) {
          setTreatmentImages(resolvedImages);
          setLoadingImages(false);
          return;
        }
      }

      if (selectedTreatment.date && patientIdCandidates.length > 0 && candidateAppointmentIds.length > 0) {
        for (const patientId of patientIdCandidates) {
          for (const appointmentId of candidateAppointmentIds) {
            const storagePrefixRef = ref(
              storage,
              `treatment-images/${patientId}/${selectedTreatment.date}/${appointmentId}`
            );
            try {
              const listing = await listAll(storagePrefixRef);
              if (listing.items.length === 0) continue;

              const imagesFromStorage = await Promise.all(
                listing.items.map(async (itemRef) => {
                  const storagePath = itemRef.fullPath;
                  try {
                    const url = await getDownloadURL(itemRef);
                    return { name: itemRef.name, url, path: storagePath };
                  } catch (error) {
                    try {
                      const encodedPath = storagePath
                        .split("/")
                        .map((segment) => encodeURIComponent(segment))
                        .join("%2F");
                      const bucket = storage.app.options.storageBucket;
                      const fallbackUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
                      return { name: itemRef.name, url: fallbackUrl, path: storagePath };
                    } catch (fallbackError) {
                      return null;
                    }
                  }
                })
              );

              const validImages = imagesFromStorage.filter(Boolean);
              if (validImages.length > 0) {
                setTreatmentImages(validImages);

                const references = validImages.map((img) => ({
                  name: img.name,
                  path: img.path,
                  url: img.url || null,
                }));
                setSelectedTreatment((prev) =>
                  prev ? { ...prev, images: references } : prev
                );

                setLoadingImages(false);
                return;
              }
            } catch (listError) {
              continue;
            }
          }
        }
      }

      setTreatmentImages([]);
    } catch (error) {
      if (
        error?.code === "storage/unauthorized" ||
        error?.code === "storage/unauthenticated" ||
        error?.message?.includes("permission")
      ) {
        setTreatmentImages([]);
      } else {
        console.error("Error loading images:", error);
        setTreatmentImages([]);
      }
    } finally {
      setLoadingImages(false);
    }
  };

  // View image in modal
  const handleViewImage = (image) => {
    setSelectedImage(image);
    setShowImageModal(true);
  };

  // Fetch user details
  const fetchUserDetails = async (uid) => {
    const patientsRef = collection(db, "users");
    try {
      const snapshot = await getDocs(patientsRef);
      let userData = null;
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (
          data.uid === uid &&
          (data.userType === "Patient" || data.role === "Patient")
        ) {
          userData = data;
        }
      });
      if (userData) setUserDetails(userData);
    } catch (error) {
      console.error("Error fetching user details:", error.message);
      showAlert(
        "Failed to load user details. Please try again.",
        "error",
        "Error"
      );
    }
  };

  // Fetch completed appointments
  const fetchCompletedAppointments = (uid) => {
    const appointmentsRef = collection(db, "appointments");
    return onSnapshot(
      appointmentsRef,
      (snapshot) => {
        let completedAppointments = [];

        snapshot.forEach((dateDoc) => {
          const dateData = dateDoc.data();

          // Iterate through all appointments for this date
          Object.entries(dateData).forEach(([appointmentId, appointment]) => {
            // Check if appointment has required fields
            if (!appointment.patientId) {
              console.warn("Appointment missing patientId:", appointment);
              return;
            }

            if (!appointment.status) {
              console.warn("Appointment missing status:", appointment);
              return;
            }

            // Check if this appointment belongs to the current user and is completed
            const appointmentPatientId =
              appointment.patientId || appointment.userId || appointment.uid;

            if (
              appointmentPatientId === uid &&
              appointment.status === "Completed"
            ) {
              // Ensure the appointment has all required fields
              const processedAppointment = {
                id: appointmentId,
                ...appointment,
                // Ensure services is always an array
                services: Array.isArray(appointment.services)
                  ? appointment.services
                  : [appointment.services || "Service"],
              };
              completedAppointments.push(processedAppointment);
            }
          });
        });

        setAppointments(completedAppointments);
        setLoading(false);
      },
      (error) => {
        // Silently handle permission errors during sign-out
        if (error.code === "permission-denied") {
          console.log("Appointments listener stopped due to sign-out");
          setAppointments([]);
          setLoading(false);
          return;
        }
        console.error("Error fetching appointments:", error);
        setAppointments([]);
        setLoading(false);
        showAlert(
          "Failed to load treatment history. Please try again.",
          "error",
          "Error"
        );
      }
    );
  };

  // Insurance modal handlers
  const handleViewInsuranceDetails = (appointment) => {
    setInsuranceDetails(appointment.insuranceDetails);
    setShowInsuranceForm(true);
  };

  const handleInsuranceClose = () => {
    setShowInsuranceForm(false);
    setInsuranceDetails(null);
  };

  // Group appointments by month and year, latest month on top, January at the bottom

  const groupedAppointments = {};
  appointments.forEach((appt) => {
    if (!appt.date) {
      return;
    }
    const dateObj = new Date(appt.date);
    if (isNaN(dateObj.getTime())) {
      return;
    }
    const month = dateObj.toLocaleString("default", { month: "long" });
    const year = dateObj.getFullYear();
    const key = `${month} ${year}`;
    if (!groupedAppointments[key]) groupedAppointments[key] = [];
    groupedAppointments[key].push(appt);
  });

  // Sort months: latest first, January at the bottom
  const sortedMonthKeys = Object.keys(groupedAppointments).sort((a, b) => {
    const [yearA, monthA] = a.split("-");
    const [yearB, monthB] = b.split("-");
    const dateA = new Date(`${monthA} 1, ${yearA}`);
    const dateB = new Date(`${monthB} 1, ${yearB}`);
    return dateB - dateA;
  });

  // Add this helper function above your component definition or inside the component
  function parseTimeToMinutes(time) {
    if (!time || typeof time !== "string" || !time.includes(":")) return null;
    const [hourStr, minuteStr] = time.split(":");
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);
    if (isNaN(hour) || isNaN(minute)) return null;
    return hour * 60 + minute;
  }

  function formatTime(minutes) {
    if (minutes === null || minutes === undefined) return "";
    const hour = Math.floor(minutes / 60);
    const minute = minutes % 60;
    const ampm = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12;
    const formattedMinute = minute < 10 ? `0${minute}` : minute;
    return `${formattedHour}:${formattedMinute} ${ampm}`;
  }


  const [treatmentsPage, setTreatmentsPage] = useState(1);
  useEffect(() => {
    setTreatmentsPage(1);
  }, [searchTerm, appointments]);

  // Build filtered appointments list for search
  const filteredAppointmentsList = appointments.filter((appt) => {
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return true;
    const dateStr = appt.date ? formatDateMMDDYYYY(appt.date) : "";
    const serviceStr = Array.isArray(appt.services)
      ? appt.services
          .map((s) => (typeof s === "object" && s.name ? s.name : s))
          .join(", ")
      : appt.services || "";
    const monthStr = appt.date
      ? new Date(appt.date)
          .toLocaleString("default", { month: "long" })
          .toLowerCase()
      : "";
    return (
     dateStr.toLowerCase().includes(q) ||
      serviceStr.toLowerCase().includes(q) ||
      monthStr.includes(q)
    );
  });

   // Build years list (unique) from ALL appointments, sorted earliest → latest
  const years = Array.from(
    new Set(
      appointments
        .map((a) => {
          if (!a.date) return null;
          const d = new Date(a.date);
          return isNaN(d.getTime()) ? null : d.getFullYear();
        })
        .filter(Boolean)
    )
  ).sort((a, b) => a - b);

  // If the search term contains a year (e.g. "2025") and that year exists,
  // put that year first so page 1 corresponds to the searched year.
  const displayedYears = React.useMemo(() => {
    const q = (searchTerm || "").trim();
    const match = q.match(/\b(19|20)\d{2}\b/); // match four-digit year
    if (match) {
      const yearNum = Number(match[0]);
      if (years.includes(yearNum)) {
        const rest = years.filter((y) => y !== yearNum);
        return [yearNum, ...rest];
      }
    }
    return years;
  }, [years, searchTerm]);

  const totalTreatmentPages = Math.max(1, displayedYears.length || 1);

  // keep current page in range if years change
  useEffect(() => {
    if (treatmentsPage > totalTreatmentPages) {
      setTreatmentsPage(totalTreatmentPages);
    }
  }, [totalTreatmentPages, treatmentsPage]);

  // selected year for current page (default earliest-first unless search promoted a year)
  const selectedYear = displayedYears.length ? displayedYears[treatmentsPage - 1] : null;

  // appointments for the selected year (and filtered by search)
  const currentTreatmentItems = filteredAppointmentsList.filter((appt) => {
    if (!selectedYear || !appt.date) return false;
    const d = new Date(appt.date);
    if (isNaN(d.getTime())) return false;
    return d.getFullYear() === selectedYear;
  });

  // Group current year's items by month/year for rendering
  const paginatedGrouped = {};
  currentTreatmentItems.forEach((appt) => {
    if (!appt.date) return;
    const dateObj = new Date(appt.date);
    if (isNaN(dateObj.getTime())) return;
    const month = dateObj.toLocaleString("default", { month: "long" });
    const year = dateObj.getFullYear();
    const key = `${month} ${year}`;
    if (!paginatedGrouped[key]) paginatedGrouped[key] = [];
    paginatedGrouped[key].push(appt);
  });

  // Keep months sorted with latest month first within the selected year
  const paginatedMonthKeys = Object.keys(paginatedGrouped).sort((a, b) => {
    const [monthA, yearA] = a.split(" ");
    const [monthB, yearB] = b.split(" ");
    return new Date(`${monthB} 1, ${yearB}`) - new Date(`${monthA} 1, ${yearA}`);
  });

  // Expand all months by default when data loads
  useEffect(() => {
    if (Object.keys(groupedAppointments).length > 0) {
      const allExpanded = {};
      Object.keys(groupedAppointments).forEach((monthKey) => {
        allExpanded[monthKey] = true;
      });
      setExpandedMonths(allExpanded);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 1; // one image per page
  const totalPages = Math.ceil(treatmentImages.length / itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const index = page - 1;
    if (treatmentImages[index]) {
      setSelectedImage(treatmentImages[index]);
    }
  };

  useEffect(() => {
    if (sortedMonthKeys.length === 0 && isSmallScreen) {
      setShowTooltip(false);
    }
  }, [sortedMonthKeys, isSmallScreen]);

  const tooltipContent = (
    <Box
      sx={{
        display: {
          xs: "flex",
          sm: "flex",
          md: "flex",
          lg: "none",
          xl: "none",
        },
        alignItems: "center",
        gap: 1,
      }}
    >
      <Typography variant="body3">
        Search
      </Typography>
      <IconButton
        size="small"
        onClick={handleTooltipClose}
        sx={{
          color: "#FEFEFE",
          padding: "2px",
          "&:hover": { opacity: 0.8 },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const accordionStylesFaqs = {
    // margin: "10px auto",
    backgroundColor: "transparent",
    // backgroundColor: "#FDF9F3",
    transition: "0.2s ease-in-out",
  };

  const boxStylesContent = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: "#FDF9F3",
    // width: "100%"
  };

  return (
    <div>
      {/* All Content */}
      <Box sx={boxStylesContent}>
        {/* Sidebar */}
        <Box
          display={{
            xs: "none",
            sm: "none",
            md: "block",
            lg: "block",
            xl: "block",
          }}>
        <Sidebar
          fName={userDetails?.firstName}
          lName={userDetails?.lastName}
          email={userDetails?.email}
        />
        </Box>

        {/*Grids */}
        <Grid
          container
          // rowSpacing={2}
          // columnSpacing={2}
          sx={{
            width: "100%",
            height: "100vh",
            // backgroundColor: "blue",
            "& .MuiGrid-item": {
              display: "flex",
              justifyContent: "center",
              // backgroundColor: "blue",
            },
          }}
        >
          {/* Main Content */}
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 12,
              xl: 12,
            }}
            item
          >
            {/* Main Content */}
            <Box
              sx={{
                width: "100%",
                height: "100%",
                backgroundColor: "#FDF9F3",
                // backgroundColor: "#f8f5ef",
              }}
            >
              <Stack direction="column">
                <Header
                  count={2}
                  page="Treatment History"
                  handleSearch={handleSearchChange}
                  value={searchTerm}
                  check={sortedMonthKeys.length}
                />

                {/*Grids */}
                <Grid
                  container
                  sx={{
                    width: "100%",
                    // backgroundColor: "blue",
                    "& .MuiGrid-item": {
                      display: "flex",
                      justifyContent: "center",
                      // backgroundColor: "blue",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "calc(100vh - 96px)",
                      position: "relative",
                      overflow: "hidden",
                      display: "flex",
                    }}
                  >
                  <Fade in={!selectedTreatment || !isSmallScreen} timeout={600}>
                    {/* Left Split */}
                    <Slide
                      direction={selectedTreatment ? "right" : "left"}
                      in={!selectedTreatment || !isSmallScreen}
                      mountOnEnter
                      unmountOnExit
                      timeout={{ enter: 600, exit: 500 }}
                        easing={{
                          enter: "cubic-bezier(0.4, 0, 0.2, 1)",
                          exit: "cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                    >
                      <Grid 
                        size={{
                          xs: 
                            sortedMonthKeys.length === 0 ? 12 : 12,
                          sm: 
                            sortedMonthKeys.length === 0 ? 12 : 12,
                          md: 
                            sortedMonthKeys.length === 0 ? 12 :
                            selectedTreatment ? 8 :
                            12,
                          lg: 
                            sortedMonthKeys.length === 0 ? 12 : 12,
                          xl: 
                            sortedMonthKeys.length === 0 ? 12 : 12
                        }}
                        item
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          // boxShadow: "-1px 5px 10px rgba(0,0,0,0.25)",
                          height: "calc(100vh - 96px)", // 👈 fills height below the header (adjust 80px if header height changes)
                          overflow: "hidden",
                          mt: "0px",
                          transition: "all 0.4s ease-in-out",
                        }}
                      >
                        <Box
                          sx={{
                            alignItems: 
                              sortedMonthKeys.length === 0 ? "center" : null,
                            justifyContent: 
                              sortedMonthKeys.length === 0 ? "center" : null,
                            overflow: "auto",
                            // margin: "30px",
                            padding: "20px",
                            // boxShadow: "0 4px 20px rgba(0,0,0,0.20)",
                            // border: "1px solid black",
                            borderRadius: "15px",
                            minHeight: 0,
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {loading ? (
                            <div style={{ textAlign: "center", padding: "20px" }}>
                              <p>Loading treatment history...</p>
                            </div>
                          ) : sortedMonthKeys.length === 0 ? (
                            <Box>
                              <Stack
                                direction="column"
                                spacing={3}
                                sx={{
                                  justifyContent: "center",
                                  textAlign: "center",
                                  justifyItems: "center",
                                  alignContent: "center",
                                  alignItems: "center"
                                }}>

                                  <Box
                                    sx={{
                                      backgroundColor: "rgba(46, 45, 45, 0.1)",
                                      borderRadius: "150px",
                                      padding: "20px"
                                    }}>
                                      
                                  <Icon
                                    src={require("../../../assets/icons/document.png")}
                                    size="user-image"/>
                                    </Box>

                                  <Stack
                                    direction="column"
                                    spacing={1}>

                                    <Text
                                      text="Oops looks empty here!"
                                      variant="historyService"/>

                                    <Text
                                      text="Schedule your first appointment and start your journey with us."
                                      variant="body1"/>

                                  </Stack>

                      <ButtonComp
                        variant="contained"
                        noElevate={true}
                        noRipple={true}
                        label={
                          isScheduleLoading ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <CircularProgress size={20} sx={{ color: "#FDF9F3" }} />
                              <span>Schedule Appointment</span>
                            </Box>
                          ) : "Schedule Appointment"
                        }
                        color="light"
                        paddings="default"
                        background="default"
                        font="default"
                        weight="default"
                        transform="default"
                        selected={true}
                        keys={false}
                        hoverTextColor="default"
                        hoverBackground="default"
                        hoverBorder="none"
                        noRead={isScheduleLoading}
                        type="default"
                        justify="default"
                        destination="/patient-appointment-booking"
                        open={true}/>

                              </Stack>
                            </Box>
                          ) : (
                            <div>
                            {paginatedMonthKeys.map((monthKey) => {
                                const filteredAppointments =
                                  paginatedGrouped[monthKey] || [];

                                return (
                                  <Box
                                    key={monthKey}
                                    sx={{
                                      marginBottom: "20px",
                                    }}
                                  >
                                    <Accordion
                                      elevation={0}
                                      expanded={expandedMonths[monthKey] === true}
                                      onChange={() => {
                                        setExpandedMonths((prev) => ({
                                          ...prev,
                                          [monthKey]: !prev[monthKey], // Toggle only the clicked monthKey, keep others as is
                                        }));
                                      }}
                                      sx={accordionStylesFaqs}
                                    >
                                      <AccordionSummary
                                        id="faq1-header"
                                        aria-controls="faq1-content"
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                          borderBottom:
                                            "1px solid rgba(88, 85, 78, 0.4)",
                                          "&:hover": {
                                            // padding: "10px 10px",
                                            backgroundColor:
                                              "rgba(46, 45, 45, 0.15)",
                                          },
                                        }}
                                      >
                                        <Text text={monthKey} variant="headerh2" />
                                      </AccordionSummary>

                                      <AccordionDetails>
                                        {expandedMonths[monthKey] && (
                                          <div>
                                            {filteredAppointments.length > 0 ? (
                                              filteredAppointments
                                                .sort(
                                                  (a, b) =>
                                                    new Date(b.date) -
                                                    new Date(a.date)
                                                )
                                                .map((appt) => {
                                                  const isSelected =
                                                    selectedTreatment === appt;
                                                  return (
                                                    <Stack
                                                      direction="row"
                                                      spacing={2}
                                                    >
                                                      <Box
                                                        sx={{
                                                          padding: "10px 0",
                                                        }}
                                                      >
                                                        <Icon
                                                          src={require("../../../assets/tooth.png")}
                                                          size="history"
                                                        />
                                                      </Box>

                                                      <Box
                                                        key={appt.id}
                                                        sx={{
                                                          // border: "1px solid #e0e0e0",
                                                          borderRadius: "10px",
                                                          padding: "10px",
                                                          // marginBottom: "16px",
                                                          background: isSelected
                                                            ? "rgba(46, 45, 45, 0.3)"
                                                            : "transparent",
                                                          width: "100%",
                                                          cursor: "pointer",
                                                          transition: "all 0.3s",
                                                          "&:hover": {
                                                            backgroundColor:
                                                              !isSelected
                                                                ? "rgba(46, 45, 45, 0.10)"
                                                                : null,
                                                          },
                                                        }}
                                                        onClick={() =>
                                                          setSelectedTreatment(appt)
                                                        }
                                                      >
                                                        <Text
                                                          variant="historyService"
                                                          text={
                                                            Array.isArray(
                                                              appt.services
                                                            )
                                                              ? appt.services
                                                                  .map((service) =>
                                                                    typeof service ===
                                                                      "object" &&
                                                                    service.name
                                                                      ? service.name
                                                                      : service
                                                                  )
                                                                  .join(", ")
                                                              : appt.services ||
                                                                "Service"
                                                          }
                                                        />

                                                        <Text
                                                          variant="historyDateTime"
                                                          text={
                                                            appt.date
                                                              ? formatDateMMDDYYYY(
                                                                  appt.date
                                                                )
                                                              : "N/A"
                                                          }
                                                        />

                                                        <Text
                                                          variant="historyDateTime"
                                                          text={(() => {
                                                            // Prefer startTime/endTime, fallback to time/duration, else N/A
                                                            if (appt.startTime) {
                                                              const start = parseTimeToMinutes(
                                                                appt.startTime
                                                              );
                                                              const end = appt.endTime
                                                                ? parseTimeToMinutes(
                                                                    appt.endTime
                                                                  )
                                                                : appt.duration
                                                                ? start +
                                                                  Number(appt.duration)
                                                                : null;
                                                              return end !== null
                                                                ? `${formatTime(
                                                                    start
                                                                  )} - ${formatTime(end)}`
                                                                : formatTime(start);
                                                            } else if (appt.time) {
                                                              const start = parseTimeToMinutes(
                                                                appt.time
                                                              );
                                                              const end = appt.duration
                                                                ? start +
                                                                  Number(appt.duration)
                                                                : null;
                                                              return end !== null
                                                                ? `${formatTime(
                                                                    start
                                                                  )} - ${formatTime(end)}`
                                                                : start !== null
                                                                ? formatTime(start)
                                                                : "N/A";
                                                            } else {
                                                              return "N/A";
                                                            }
                                                          })()}
                                                        />
                                                      </Box>
                                                    </Stack>
                                                  );
                                                })
                                            ) : (
                                              <div
                                                style={{
                                                  color: "#888",
                                                  textAlign: "center",
                                                  marginTop: "10px",
                                                }}
                                              >
                                                No appointments found for this
                                                month.
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </AccordionDetails>
                                    </Accordion>
                                  </Box>
                                );
                              })}
                            </div>
                          )}
                        </Box>

                      {sortedMonthKeys.length !== 0 && (
                      // {/* Pagination for treatments (left column) */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          padding: "12px 20px",
                          borderTop: "1px solid rgba(89,84,74,0.08)",
                        }}
                      >
                        <Pagination
                          count={totalTreatmentPages}
                          page={treatmentsPage}
                          onChange={(e, page) => setTreatmentsPage(page)}
                          shape="rounded"
                          variant="outlined"
                          siblingCount={1}
                          boundaryCount={1}
                          color="primary"
                          sx={{
                            "& .MuiPaginationItem-root": {
                              fontWeight: 600,
                              fontSize: "14px",
                            borderRadius: "6px",
                              minWidth: "36px",
                              minHeight: "36px",
                              border: "1px solid #C5A572",
                              backgroundColor: "#FEFEFE",
                              "&.Mui-selected": {
                                backgroundColor: "#2E2D2D",
                            },
                            },
                          }}
                        />
                      </Box>
                      )}



                        
                      </Grid>
                    </Slide>
                  </Fade> 

                  {/* Right Split */}
                  <Slide
                    direction="left"
                    in={!!selectedTreatment}
                    mountOnEnter
                    unmountOnExit
                    timeout={{ enter: 600, exit: 500 }}
                    easing={{
                      enter: "cubic-bezier(0.4, 0, 0.2, 1)",
                      exit: "cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    {/* Right Split */}
                    <Grid
                      size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 8,
                        xl: 8,
                      }}
                      item
                      sx={{
                        display: {
                          xs : sortedMonthKeys.length === 0 ? "none" : "flex",
                          sm : sortedMonthKeys.length === 0 ? "none" : "flex",
                          md : sortedMonthKeys.length === 0 ? "none" : "flex",
                          lg : sortedMonthKeys.length === 0 ? "none" : "flex",
                          xl : sortedMonthKeys.length === 0 ? "none" : "flex",
                        },
                        flexDirection: "column",
                        boxShadow: "-1px 5px 10px rgba(0,0,0,0.25)",
                        height: "calc(100vh - 96px)", // 👈 fills height below the header (adjust 80px if header height changes)
                        overflow: "hidden",
                        mt: "0px",
                        backgroundColor: "#FDF9F3",
                        transition: "all 0.4s ease-in-out",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          px: 2,
                          py: 1.5,
                          cursor: "pointer",
                          color: "#2E2D2D",
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={() => setSelectedTreatment(null)}
                      >
                        <ArrowBackIcon sx={{ mr: 1 }} />
                        <Typography fontWeight="bold">Go Back</Typography>
                      </Box>

                      {/* Right: Treatment Details */}
                      <Box
                        sx={{
                          minHeight: 0,
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            padding: "30px",
                            marginTop: 0,
                            minHeight: 420,
                            height: "100%",
                            background: "inherit",
                            overflowY: "auto",
                          }}
                        >
                          {selectedTreatment ? (
                            <>
                              <Stack direction="column" spacing={3}>
                                {/* Remarks */}
                                <Text text="Remarks" variant="h4Dark" />

                                {/* Remarks Box */}
                                <Box
                                  sx={{
                                    backgroundColor: "#FEFEFE",
                                    height: "100px",
                                    boxShadow: "0 -1px 6px rgba(0,0,0,.5)",
                                    borderRadius: "10px",
                                    padding: "20px",
                                    overflow: "auto",
                                  }}
                                >
                                  {/* Remarks */}
                                  <Text
                                    text={selectedTreatment.dentistRemarks || selectedTreatment.remarks || "None."}
                                    variant="historyRemarks"
                                  />
                                </Box>

                                <Divider
                                  sx={{
                                    backgroundColor: "rgba(89, 84, 74, 0.2)",
                                  }}
                                />

                                {/* Details */}
                                <Stack direction="column" spacing={1}>
                                  <Text text="Details" variant="h4Dark" />

                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                  >
                                    <Text
                                      text="Service"
                                      variant="historyLabels"
                                    />

                                    <Text
                                      text={
                                        Array.isArray(selectedTreatment.services)
                                          ? selectedTreatment.services
                                              .map((service) =>
                                                typeof service === "object" &&
                                                service.name
                                                  ? service.name
                                                  : service
                                              )
                                              .join(", ")
                                          : selectedTreatment.services ||
                                            "Service"
                                      }
                                      variant="historyContent"
                                    />
                                  </Stack>

                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                  >
                                    <Text text="Date" variant="historyLabels" />

                                    <Text
                                      text={
                                        selectedTreatment.date
                                          ? formatDateMMDDYYYY(
                                              selectedTreatment.date
                                            )
                                          : "N/A"
                                      }
                                      variant="historyContent"
                                    />
                                  </Stack>

                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                  >
                                    <Text text="Time" variant="historyLabels" />

                                    <Text
                                      text={(() => {
                                        // Prefer startTime/endTime, fallback to time/duration, else N/A
                                        if (selectedTreatment.startTime) {
                                          const start = parseTimeToMinutes(
                                            selectedTreatment.startTime
                                          );
                                          const end = selectedTreatment.endTime
                                            ? parseTimeToMinutes(
                                                selectedTreatment.endTime
                                              )
                                            : selectedTreatment.duration
                                            ? start +
                                              Number(selectedTreatment.duration)
                                            : null;
                                          return end !== null
                                            ? `${formatTime(
                                                start
                                              )} - ${formatTime(end)}`
                                            : formatTime(start);
                                        } else if (selectedTreatment.time) {
                                          const start = parseTimeToMinutes(
                                            selectedTreatment.time
                                          );
                                          const end = selectedTreatment.duration
                                            ? start +
                                              Number(selectedTreatment.duration)
                                            : null;
                                          return end !== null
                                            ? `${formatTime(
                                                start
                                              )} - ${formatTime(end)}`
                                            : start !== null
                                            ? formatTime(start)
                                            : "N/A";
                                        } else {
                                          return "N/A";
                                        }
                                      })()}
                                      variant="historyContent"
                                    />
                                  </Stack>

                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                  >
                                    <Text text="Doctor" variant="historyLabels" />

                                    <Text
                                      text={formatDentistName(
                                        selectedTreatment.dentist
                                      )}
                                      variant="historyContent"
                                    />
                                  </Stack>
                                </Stack>

                                <Divider
                                  sx={{
                                    backgroundColor: "rgba(89, 84, 74, 0.2)",
                                  }}
                                />

                                {/* Images */}
                                <Stack
                                  direction="column"
                                  spacing={2}
                                  sx={{
                                    border: null,
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={2}
                                    sx={{
                                      alignItems: "center",
                                    }}
                                  >
                                    <Text text="Images" variant="h4Dark" />

                                    {treatmentImages.length > 0 && (
                                      <Box
                                        sx={{
                                          backgroundColor:
                                            "rgba(44, 37, 30, 0.5)",
                                          width: "25px",
                                          margin: "0 10px",
                                          justifyContent: "center",
                                          justifyItems: "center",
                                          borderRadius: "2px",
                                          textAlign: "center",
                                          color: "#FEFEFE",
                                        }}
                                      >
                                        {" "}
                                        {treatmentImages.length}
                                      </Box>
                                    )}
                                  </Stack>

                                  {loadingImages ? (
                                    <div
                                      style={{
                                        textAlign: "center",
                                        color: "#666",
                                        padding: "20px",
                                        background: "transparent",
                                        // borderRadius: "8px"
                                      }}
                                    >
                                      Loading images...
                                    </div>
                                  ) : treatmentImages.length > 0 ? (
                                    <Stack
                                      direction="row"
                                      sx={{
                                        // width: "100%",
                                        borderRadius: "10px",
                                        // padding: "10px",
                                        justifyContent: "center",
                                        alignItems: "center",
                                      }}
                                    >
                                      <AvatarGroup
                                        max={5}
                                        sx={{
                                          "& .MuiAvatar-root": {
                                            bgcolor: "#59544A",
                                            cursor: "pointer",
                                            border: null,
                                          },
                                        }}
                                      >
                                        {treatmentImages.map((image, index) => (
                                          <Box
                                            key={image.path || index}
                                            onClick={() => handleViewImage(image)}
                                            sx={{
                                              display: "inline-flex",
                                              width: "100%",
                                              borderRadius: "50%",
                                              backgroundColor: "#59544A",
                                              padding: "2px",
                                              marginRight:
                                                treatmentImages.length === 1
                                                  ? null :
                                                treatmentImages.length < 5
                                                  ? "-20px"
                                                  : null,
                                              ":hover": {
                                                backgroundColor:
                                                  "rgba(46, 45, 45, 0.2)",
                                                // opacity: 0.8
                                              },
                                            }}
                                          >
                                            <Tooltip
                                              title="View Image"
                                              // placement="top"
                                              arrow
                                            >
                                              <Avatar
                                                alt={`Treatment image ${
                                                  index + 1
                                                }`}
                                                src={image.url}
                                                sx={{
                                                  // border: "3px solid #59544A",
                                                  backgroundColor: "#59544A", // ensures black background for each avatar
                                                  color: "white", // (optional) better contrast if no image
                                                }}
                                                onError={(e) => {
                                                  e.target.src =
                                                    PLACEHOLDERS.DENTIST_80;
                                                }}
                                              />
                                            </Tooltip>
                                          </Box>
                                        ))}
                                      </AvatarGroup>
                                    </Stack>
                                  ) : (
                                    <div
                                      style={{
                                        textAlign: "center",
                                        color: "#666",
                                        padding: "20px",
                                        background: "#f8f9fa",
                                        borderRadius: "8px",
                                        border: "2px dashed #dee2e6",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "16px",
                                          marginBottom: "8px",
                                          fontWeight: "500",
                                        }}
                                      >
                                        No treatment images available
                                      </div>
                                      <div
                                        style={{ fontSize: "14px", opacity: 0.8 }}
                                      >
                                        Images will appear here once uploaded by
                                        the clinic personnel
                                      </div>
                                    </div>
                                  )}
                                </Stack>

                                <Divider
                                  sx={{
                                    backgroundColor: "rgba(89, 84, 74, 0.2)",
                                  }}
                                />

                                {selectedTreatment.insuranceDetails && (
                                  <>
                                    {/* Insurance */}
                                    <Text text="Insurance" variant="h4Dark" />

                                    {/* View Insurance */}
                                    <div
                                      style={{
                                        marginBottom: "16px",
                                        textAlign: "end",
                                      }}
                                    >
                                      <div>
                                        <button
                                          onClick={() =>
                                            handleViewInsuranceDetails(
                                              selectedTreatment
                                            )
                                          }
                                          style={{
                                            background: "#007BFF",
                                            color: "white",
                                            border: "none",
                                            padding: "8px 18px",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontSize: "15px",
                                            fontWeight: "bold",
                                          }}
                                        >
                                          View Insurance
                                        </button>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </Stack>
                            </>
                          ) : (
                            <Text text="Click on a treatment." variant="h4Dark" />
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Slide>
                  </Box>

                  {/* Image View Modal */}
                  {showImageModal && selectedImage && (
                    <Modal
                      isOpen={showImageModal}
                      onRequestClose={() => setShowImageModal(false)}
                      contentLabel="All Notifications"
                      style={{
                        overlay: {
                          zIndex: 9999,
                          backgroundColor: "rgba(0, 0, 0, 0.7)",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          padding: "16px",
                        },
                        content: {
                          // inset: "unset",
                          border: "none",
                          borderRadius: "16px",
                          background: "#FEFEFE",
                          padding: "24px",
                          width: "75%",
                          maxHeight: "85vh",
                          overflowY: "auto",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                          // Responsive adjustments
                          transition: "all 0.3s ease",
                          top: "50%",
                          left: "50%",
                          right: "auto",
                          bottom: "auto",
                          // margin: "20px",
                          marginRight: "-50%",
                          transform: "translate(-50%, -50%)",
                          maxWidth: "600px",
                          minWidth: "180px"
                        },
                      }}
                    >
                      {/*Grids */}
                <Grid
                  container
                  sx={{
                    width: "100%",
                    // backgroundColor: "blue",
                    "& .MuiGrid-item": {
                      display: "flex",
                      justifyContent: "center",
                      // backgroundColor: "blue",
                    },
                  }}
                >
                  <Grid
                                    size={{
                                      xs: 12,
                                      sm: 12,
                                      md: 12,
                                      lg: 12,
                                      xl: 12,
                                    }}
                                    item
                                  >
                      <Stack direction="column" spacing={3}>
                        {/* Close Button */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "end",
                            position: "sticky",
                          }}
                        >
                          <Button
                            disableRipple
                            onClick={() => setShowImageModal(false)}
                            sx={{
                              background: "none",
                              color: "#2C251E",
                              border: "1px solid #2C251E",
                              padding: "7px",
                              borderRadius: "50px",
                              cursor: "pointer",
                              minWidth: "40px",
                              minHeight: "40px",
                              ":hover": {
                                background: "rgba(0, 0, 0, 0.1)",
                              },
                            }}
                          >
                            X
                          </Button>
                        </Box>

                        <Stack direction="column" spacing={5}>
                          {/* Image Container */}
                          <Box
                            sx={{
                              display: "flex",
                              // position: "sticky",
                              // backgroundColor: "mediumaquamarine",
                              border: "1px solid #59544A",
                              justifyContent: "center",
                            }}
                          >
                            {/* Image */}
                            <img
                              src={selectedImage.url}
                              alt="Treatment image"
                              style={{
                                // minHeight: "500px",
                                width: "100%",
                                maxWidth: "500px",
                                maxHeight: "500px",
                              }}
                            />
                          </Box>

                          {/* Pagination */}
                          <Box justifyItems="center">
                            <Pagination
                              count={totalPages}
                              page={currentPage}
                              onChange={(e, page) => handlePageChange(page)}
                              shape="rounded"
                              variant="outlined"
                              siblingCount={1}
                              boundaryCount={2}
                              color="primary"
                              sx={{
                                "& .MuiPaginationItem-root": {
                                  fontWeight: 600,
                                  fontSize: "16px",
                                  borderRadius: "6px",
                                  minWidth: "40px",
                                  minHeight: "40px",
                                  border: "1px solid #C5A572",
                                  backgroundColor: "#FEFEFE",
                                  "&.Mui-selected": {
                                    backgroundColor: "#2E2D2D",
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Stack>
                      </Stack>
                      </Grid>
                      </Grid>
                    </Modal>
                  )}
                </Grid>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <ViewInsurance
        isOpen={showInsuranceForm}
        onClose={handleInsuranceClose}
        insuranceDetails={insuranceDetails}
      />

      {/* Custom Alert Modal */}
      <CustomAlert
        isOpen={alertConfig.isOpen}
        onClose={closeAlert}
        message={alertConfig.message}
        type={alertConfig.type}
        title={alertConfig.title}
      />

      <SplashScreen
        isOpen={showCustomAlert}
      />
    </div>
  );
};

export default PatientRecord;
